import type {
  ApiPaymentRecord,
  CreatePaymentRecordPayload,
} from "@src/packages/costs/session-cost-records";
import {
  hasCostDocumentUpload,
  mergeCostDocumentReferences,
  toAttachedCostDocumentReference,
} from "@src/packages/costs/document-linkage";
import type {
  ManualPaymentEntry,
  UploadedCostDocument,
} from "./suggested/CostsOverviewConceptScreen";
import type { TaxReviewEntry } from "@src/packages/costs/taxReview";
import type { TaxDeductionCalculation } from "@src/packages/costs/hooks/useTaxDeductions";

export type ItemizationChoice = "yes" | "no" | "unknown";
type TaxReviewStatusFields = Pick<
  TaxReviewEntry,
  "reviewStatus" | "missingReasons"
> & { record?: Pick<TaxReviewEntry["record"], "id"> };
export type TaxCalculationLineItem =
  TaxDeductionCalculation["lineItems"][number];

function hasAttentionReason(
  reasons: readonly string[],
  reason: string,
): boolean {
  return reasons.some((candidate) => candidate === reason);
}

function effectiveStatus(
  entry: TaxReviewEntry,
  lineItem?: TaxCalculationLineItem,
) {
  if (!lineItem) return entry.reviewStatus;
  if (lineItem.status === "excluded") return "excluded" as const;
  if (lineItem.status === "included") return "included" as const;
  return "needs-review" as const;
}

export function itemizationChoice(
  value: boolean | null | undefined,
): ItemizationChoice {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "unknown";
}

export function itemizationValue(value: ItemizationChoice): boolean | null {
  if (value === "yes") return true;
  if (value === "no") return false;
  return null;
}

export function isItemizationChoice(value: string): value is ItemizationChoice {
  return value === "yes" || value === "no" || value === "unknown";
}

export function getTaxReviewStatusTagTone(
  entry: TaxReviewEntry,
  lineItem?: TaxCalculationLineItem,
) {
  const status = effectiveStatus(entry, lineItem);
  const missingReasons = lineItem?.attentionReasons ?? entry.missingReasons;
  if (status === "excluded") return "coral" as const;
  if (missingReasons.includes("not_medical_expense")) return "coral" as const;
  if (status === "included" && missingReasons.length === 0) {
    return "green" as const;
  }
  return "gold" as const;
}

export function getTaxReviewStatusTranslationKey(
  entry: TaxReviewEntry,
  lineItem?: TaxCalculationLineItem,
) {
  const status = effectiveStatus(entry, lineItem);
  const attentionReasons = lineItem?.attentionReasons ?? entry.missingReasons;
  if (status === "excluded") {
    return "costs.tax_review.excluded_from_review";
  }
  if (
    hasAttentionReason(attentionReasons, "missing_receipt") ||
    hasAttentionReason(attentionReasons, "payment_document")
  ) {
    return "costs.tax_review.missing_receipt";
  }
  if (
    hasAttentionReason(attentionReasons, "missing_provider") ||
    hasAttentionReason(attentionReasons, "provider")
  ) {
    return "costs.tax_review.missing_provider";
  }
  if (hasAttentionReason(attentionReasons, "missing_payer_allocation")) {
    return "costs.tax_review.missing_payer_allocation";
  }
  if (
    hasAttentionReason(attentionReasons, "missing_insurance_outcome") ||
    hasAttentionReason(attentionReasons, "insurance_outcome")
  ) {
    return "costs.tax_review.missing_insurance_outcome";
  }
  if (hasAttentionReason(attentionReasons, "not_medical_expense")) {
    return "costs.tax_review.not_medical_expense";
  }
  if (
    hasAttentionReason(attentionReasons, "missing_eligibility_context") ||
    hasAttentionReason(attentionReasons, "eligibility_context")
  ) {
    return "costs.tax_review.missing_eligibility_context";
  }
  if (hasAttentionReason(attentionReasons, "missing_review_decision")) {
    return "costs.tax_review.needs_your_decision";
  }
  if (status === "included") {
    return "costs.tax_review.included_in_review";
  }
  return "costs.tax_review.needs_your_decision";
}

export function taxReviewEntryNeedsAttention(
  entry: TaxReviewStatusFields,
  lineItem?: TaxCalculationLineItem,
) {
  if (lineItem) return lineItem.status === "needs_attention";
  return (
    entry.reviewStatus === "needs-review" || entry.missingReasons.length > 0
  );
}

export function orderTaxReviewEntries<T extends TaxReviewStatusFields>(
  entries: T[],
  lineItemsByRecordId?: ReadonlyMap<string, TaxCalculationLineItem>,
) {
  return [...entries].sort(
    (left, right) =>
      Number(
        taxReviewEntryNeedsAttention(
          right,
          right.record ? lineItemsByRecordId?.get(right.record.id) : undefined,
        ),
      ) -
      Number(
        taxReviewEntryNeedsAttention(
          left,
          left.record ? lineItemsByRecordId?.get(left.record.id) : undefined,
        ),
      ),
  );
}

export function paymentRecordStatusForKind(
  kind: ManualPaymentEntry["kind"],
): CreatePaymentRecordPayload["status"] {
  if (kind === "payment") return "paid";
  if (kind === "claim") return "submitted";
  if (kind === "receipt") return "needs-receipt";
  return "due";
}

export function findPaymentRecordForUpload(
  records: ApiPaymentRecord[],
  uploadId: string,
) {
  return records.find((record) =>
    hasCostDocumentUpload(record.documents ?? [], uploadId),
  );
}

export function buildManualPaymentRecordPayload(
  entry: ManualPaymentEntry,
  options: {
    missingDocumentLinkageMessage: string;
    parseDate: (value: string) => string | null | undefined;
    today: string;
  },
): CreatePaymentRecordPayload {
  if (entry.attachmentName && !entry.attachmentDocumentId) {
    throw new Error(options.missingDocumentLinkageMessage);
  }

  return {
    kind: entry.kind,
    entrySource: "manual",
    label: entry.label,
    provider: entry.provider,
    amount: entry.amount,
    billedAmount: entry.amount,
    status: paymentRecordStatusForKind(entry.kind),
    occurredOn: options.parseDate(entry.date) ?? options.today,
    note: entry.note || undefined,
    documents:
      entry.attachmentName && entry.attachmentDocumentId
        ? [
            {
              id: entry.attachmentDocumentId,
              label: entry.attachmentName,
              status: "attached" as const,
              uploadId: entry.attachmentUploadId ?? null,
            },
          ]
        : [],
  };
}

export function buildUploadedPaymentRecordPayload(
  document: UploadedCostDocument,
  options: {
    missingDocumentLinkageMessage: string;
    uploadedDocumentLabel: (kind: ManualPaymentEntry["kind"]) => string;
    uploadedPhotoLabel: string;
    uploadedFileLabel: string;
    today: string;
  },
): CreatePaymentRecordPayload {
  if (!document.documentId || !document.uploadId) {
    throw new Error(options.missingDocumentLinkageMessage);
  }

  const kind = document.paymentKind ?? "payment";
  return {
    kind,
    entrySource: "document_upload",
    label: options.uploadedDocumentLabel(kind),
    provider:
      document.source === "camera"
        ? options.uploadedPhotoLabel
        : options.uploadedFileLabel,
    amount: 0,
    billedAmount: 0,
    status: paymentRecordStatusForKind(kind),
    occurredOn: options.today,
    documents: [
      toAttachedCostDocumentReference({
        documentId: document.documentId,
        uploadId: document.uploadId,
        name: document.name,
      }),
    ],
  };
}

export function attachDocumentToPaymentRecord(
  record: ApiPaymentRecord,
  document: UploadedCostDocument,
  missingDocumentLinkageMessage: string,
) {
  if (!document.documentId || !document.uploadId) {
    throw new Error(missingDocumentLinkageMessage);
  }

  return mergeCostDocumentReferences(
    record.documents ?? [],
    toAttachedCostDocumentReference({
      documentId: document.documentId,
      uploadId: document.uploadId,
      name: document.name,
    }),
  );
}
